import { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { Card, Tooltip } from 'antd';
import { useTranslate, TFnType } from '@tolgee/react';
import { initEcharts, EChartsOption } from 'lib/echarts';
import dayjs from 'lib/dayjs';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useDashboardCurrency } from 'views/Dashboard/ImplementData/hooks/useCurrency';
import Empty from 'components/Empty';
import { useSize } from 'ahooks';
import { useDashboardFilterContext } from '../Filter/useFilter';

const Container = styled(Card)`
  margin-top: 16px;
  border-radius: 4px;
  height: 420px;
  background: #ffffff;
  width: 100%;
  .ant-card-head {
    .ant-card-head-title {
      .before {
        height: 14px;
        border-left: 4px solid #3370ff;
        display: inline-block;
        margin-right: 4px;
      }
      .anticon {
        margin-left: 4px;
      }
    }
  }
  .ant-card-body {
    display: flex;
    .ant-row {
      height: 50px;
      margin-bottom: 12px;
      background-color: #f5f9fe;
      border-radius: 4px;
      align-items: center;
      padding: 0 16px;
      .title {
        color: #84868c;
      }
      .num {
        color: #515359;
        font-size: 16px;
      }
    }
  }
  .chart {
    position: relative;
    width: 100%;
    .ant-empty {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
    }
  }
  .bar {
    flex: 1;
    height: 330px;
  }
`;

interface Props {
  value?: Dashboard.OrderOverviewInner;
}

const barOption = (value: Dashboard.OrderOverviewInner, t: TFnType): EChartsOption => {
  const data = value.每日退单 || [];
  const dateRange = data.map((x) => x.date).map((x) => dayjs(x).format('MM-DD'));
  const dataZoom = [
    {
      show: true,
      realtime: true,
      start: 0,
      end: 100,
    },
    {
      type: 'inside',
      realtime: true,
      start: 0,
      end: 100,
    },
  ];
  return {
    dataZoom: dateRange.length > 10 ? dataZoom : undefined,
    legend: {
      data: [t('ViewDashboard.取消订单量'), t('ViewDashboard.退货/退款量')],
    },
    xAxis: {
      type: 'category',
      boundaryGap: true,
      data: dateRange,
    },
    yAxis: [
      {
        type: 'value',
      },
    ],
    tooltip: {
      trigger: 'axis',
    },
    series: [
      {
        data: data.map((x) => x.取消订单),
        type: 'bar',
        name: t('ViewDashboard.取消订单量'),
        itemStyle: {
          color: '#5B8FF9',
          opacity: 0.85,
        },
      },
      {
        data: data.map((x) => x['退货/退款量']),
        type: 'bar',
        name: t('ViewDashboard.退货/退款量'),
        itemStyle: {
          color: '#5AD8A6',
          opacity: 0.85,
        },
      },
    ],
  };
};

interface Props {
  value?: Dashboard.OrderOverviewInner;
}

const Return = (props: Props) => {
  const { value } = props;
  const { t } = useTranslate();
  const { hasTokopedia } = useDashboardFilterContext();
  const { currency } = useDashboardCurrency();
  const chartDomRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts>();
  const { width } = useSize(chartDomRef) || {};

  useEffect(() => {
    chartRef.current?.resize();
  }, [width]);
  useEffect(() => {
    if (!value || !chartDomRef.current) {
      return;
    }
    if (!chartRef.current) {
      chartRef.current = initEcharts(chartDomRef.current);
    }
    const options = barOption(value, t);
    chartRef.current.setOption(options);
  }, [value, t, currency?.symbol]);
  return (
    <Container
      title={
        <div>
          <span className="before" />
          {t('aide.弃单情况')}
          <Tooltip
            title={
              <div>
                {t('aide.取消订单和退货/退款统称为弃单')}
                {hasTokopedia && <div>{t('dashboard_tokopedia_tips1')}</div>}
              </div>
            }
          >
            <QuestionCircleOutlined />
          </Tooltip>
        </div>
      }
    >
      <div className="chart">
        <div className="bar" ref={chartDomRef} />
        {value?.每日退单?.length ? null : <Empty />}
      </div>
    </Container>
  );
};

export default Return;
